import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { adminDemoData } from '../../../../data/AdminDemoData';
import { useInit } from '../../../Contexts/InitProvider';
import LibModal from '../../../features/LibModal/LibModal';
import { useTemplate } from '../../../features/template/TemplateProvider';
import { ApiDisable2fa, ApiLoadAdminsData } from '../AdminsPageOperation';
import AdminStatusChangeModal from '../modals/AdminStatusChangeModal';
import EditAdminModal from '../modals/EditAdminModal';

const AdminsListContext = createContext();
export const useAdminListInfo = () => useContext(AdminsListContext);

function AdminsListProvider({ children }) {
    const [modalInfo, setModalInfo] = useState({
        type: '',
        data: {}
    });
    const { setMessage, setConfirm } = useTemplate();
    const [reloadKey, setReloadKey] = useState(0);
    const [infoReloadKey, setInfoReloadKey] = useState(0);
    const [params, setParams] = useState({});
    const [adminListData, setAdminListData] = useState(adminDemoData);
    const [editAdminModal, setEditAdminModal] = useState({
        show: false,
        data: {}
    });

    const { initInfo } = useInit();

    // -- Load all customer initially
    useEffect(() => {
        setAdminListData(adminDemoData);
        if (Object.keys(params).length > 0) {
            ApiLoadAdminsData(params)
                .then(({ data }) => {
                    setAdminListData(data?.users);
                })
                .catch((err) => {
                    setAdminListData([]);
                    setMessage({ type: 'error', text: err.toString(0) });
                });
        }
    }, [params, setMessage, reloadKey]);

    const value = useMemo(() => {
        // --Handle Reload
        const handleReload = () => {
            setReloadKey((d) => d + 1);
        };

        const openModal = (type, data) => {
            setModalInfo({ type, data });
        };
        const closeModal = () => {
            setModalInfo({ type: '', data: {} });
        };

        // -- Edit Customer Modal
        const handleShowEditModal = (e, data) => {
            e.preventDefault();
            e.stopPropagation();
            setEditAdminModal({ show: true, data });
        };

        // -- Change Customer Status Handler
        const handleCustomerStatusModal = (e, data) => {
            e.stopPropagation();
            setConfirm({
                showButton: false,
                text: (
                    <AdminStatusChangeModal
                        data={data}
                        setReloadKey={setReloadKey}
                        setInfoReloadKey={setInfoReloadKey}
                    />
                )
            });
        };

        const handleDisable2FA = (e, id) => {
            e.stopPropagation();
            setConfirm({
                text: 'Disabling Two-Factor Authentication will remove an additional layer of security on this account. Are you sure you want to proceed?',
                textAction: 'Yes',
                clickAction: () => {
                    ApiDisable2fa(id)
                        .then(({ title }) => {
                            setMessage({ type: 'success', text: title });
                            setConfirm(null);
                            handleReload();
                        })
                        .catch((err) => {
                            setMessage({ type: 'error', text: err.toString() });
                        });
                }
            });
        };

        return {
            // -- Reload Key
            reloadKey,
            setReloadKey,
            infoReloadKey,
            setInfoReloadKey,
            // -- Params
            params,
            setParams,
            // -- customer list
            adminListData,
            setAdminListData,

            // -- Edit Customer Modal
            editAdminModal,
            setEditAdminModal,
            // All Handler
            handleReload,

            // -- Edit Customer Modal
            handleShowEditModal,
            // -- Customer Status Change Modal
            handleCustomerStatusModal,
            // -- Disable 2FA
            handleDisable2FA,
            // -- Open Modal
            openModal,
            closeModal,
            // -- Modal Info
            modalType: modalInfo.type,
            modalData: modalInfo.data
        };
    }, [
        reloadKey,
        infoReloadKey,
        params,
        adminListData,
        editAdminModal,
        modalInfo,
        setConfirm,
        initInfo?.roles,
        setMessage
    ]);

    return (
        <AdminsListContext.Provider value={value}>
            {children}
            {editAdminModal.show && (
                <LibModal
                    title="Update Admin"
                    setOpen={() => setEditAdminModal({ show: false, data: {} })}
                >
                    <EditAdminModal
                        setReloadKey={setReloadKey}
                        setInfoReloadKey={setInfoReloadKey}
                        closeEditModal={() => setEditAdminModal({ show: false, data: {} })}
                        data={editAdminModal.data}
                    />
                </LibModal>
            )}
        </AdminsListContext.Provider>
    );
}

export default AdminsListProvider;
